/**
 * PublicLayout — Shared layout for all unauthenticated public pages
 * (Landing, Pricing, Privacy, Terms, etc.).
 *
 * Provides the consistent dark canvas with:
 * - Floating background orbs + perspective grid
 * - Shared Navbar (fixed, glassmorphism)
 * - Shared PublicFooter (logo, OpenClaw attribution, legal links)
 * - <Outlet /> for page-specific content
 *
 * Each page still owns its own <SEO> component since JSON-LD schemas differ.
 */

import { Box } from '@mantine/core';
import { Outlet } from 'react-router-dom';
import { Navbar } from './Navbar';
import { PublicFooter } from './PublicFooter';

const CSS = `
  @keyframes float-slow {
    0%,100% { transform: translateY(0) }
    50%     { transform: translateY(-30px) }
  }
`;

export function PublicLayout() {
  return (
    <Box style={{ minHeight: '100vh', background: '#0a0a0b', color: '#fff', overflowX: 'hidden' }}>
      <style>{CSS}</style>

      {/* ── Background Effects ── */}
      <Box
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          overflow: 'hidden',
        }}
      >
        {/* Perspective grid */}
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(253,126,20,.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(253,126,20,.03) 1px, transparent 1px)
            `,
            backgroundSize: '64px 64px',
            maskImage:
              'radial-gradient(ellipse 80% 50% at 50% 0%, black 20%, transparent 70%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 80% 50% at 50% 0%, black 20%, transparent 70%)',
          }}
        />
        {/* Orange orb */}
        <Box
          style={{
            position: 'absolute',
            width: 700,
            height: 700,
            borderRadius: '50%',
            background: '#fd7e14',
            filter: 'blur(150px)',
            opacity: 0.07,
            top: -300,
            right: -200,
            animation: 'float-slow 20s ease-in-out infinite',
          }}
        />
        {/* Navy orb */}
        <Box
          style={{
            position: 'absolute',
            width: 700,
            height: 700,
            borderRadius: '50%',
            background: '#1a365d',
            filter: 'blur(150px)',
            opacity: 0.09,
            bottom: -300,
            left: -200,
            animation: 'float-slow 25s ease-in-out infinite reverse',
          }}
        />
      </Box>

      <Navbar />

      {/* Page content — z-index above background, padded for fixed navbar */}
      <Box style={{ position: 'relative', zIndex: 1 }}>
        <Outlet />
      </Box>

      <PublicFooter />
    </Box>
  );
}
